import { useEffect, useState, type ReactNode } from "react";
import { create } from "zustand";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { useRun } from "@/state/run";
import { useSceneStore } from "@/engine/SceneManager";
import {
    type ChapterIntroDef,
} from "@/components/ChapterIntroBanner";
import {
    getScenarioUpcomingIntel,
    formatScenarioIntelEta,
} from "@/content/scenarioPresentation";
import { getScenarioChapters } from "@/content/scenarioChapters";
import { projectFrontierTrajectory, summarizeFrontierTrajectoryFootprints } from "@/utils/frontierEndingProjection";
import { EndingCompass } from "@/components/EndingCompass";
import type { ScenarioCompanion, ScenarioCompanionState, ScenarioStateResponse } from "@/api/types";
import { memoriesForCompanion } from "@/content/companionMemories";
import { TrainingProgressPanel } from "@/components/TrainingProgressPanel";

/**
 * 2026-04-23: 외부(예: CompanionCompactStrip 의 "자세히" 버튼) 에서도 drawer 를
 * 열 수 있도록 전역 토글 스토어. 기존 `?` 키/엣지 탭 경로와 공존.
 */
interface SituationDrawerStore {
    open: boolean;
    toggle: () => void;
    setOpen: (open: boolean) => void;
}
export const useSituationDrawer = create<SituationDrawerStore>((set) => ({
    open: false,
    toggle: () => set((s) => ({ open: !s.open })),
    setOpen: (open) => set({ open }),
}));

/**
 * 전역 "상황 드로어" — 우측 엣지 탭으로 언제든 펴 볼 수 있는 요약 장부.
 *
 * <p>2026-04-22 E23: 플레이어 피드백 "지금 어디 있는지 모르겠다" 의 최종 안전망.
 * 씬 이동 없이 언제든 현재 장·주차·엔딩 궤적·원정 상태·동료·다음 사건·최근 사건을
 * 한 화면에 펼쳐 본다. CharacterSheet 전체 전환 없이 "한 눈에" 훑는 glance 뷰.
 *
 * <p>활성화: 우측 세로 엣지 탭 클릭, 또는 `?` 키 토글, 또는 Esc 키로 닫기.
 *
 * <p>자동 은닉: run 이 없으면(Home/RunForge/Splash) 탭 자체 미노출.
 * 플레이 씬에서만 등장.
 */
/**
 * 2026-04-23: 장부 탭이 어느 씬에서 보일지 정의. 사용자 피드백:
 * "아직 시나리오 진입도 안 했는데 장부 탭이 떠 있다".
 * run 이 캐시되어 있어도 home/run-forge/splash 에서는 숨겨야 한다.
 */
const DRAWER_SCENES: ReadonlySet<string> = new Set([
    "weekly-turn",
    "event",
    "combat",
    "run-result",
    "character-sheet",
    "shop",
    "tactic-editor",
]);

export function SituationDrawer() {
    const run = useRun(s => s.run);
    const push = useSceneStore(s => s.push);
    const currentSceneId = useSceneStore(s => {
        const top = s.stack[s.stack.length - 1];
        return top?.id ?? null;
    });
    const open = useSituationDrawer(s => s.open);
    const setOpen = useSituationDrawer(s => s.setOpen);
    const toggle = useSituationDrawer(s => s.toggle);

    // ESC 로 닫기
    useEffect(() => {
        if (!open) return;
        const h = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [open, setOpen]);

    // '?' 키 토글 — input/textarea 포커스 중에는 무시 (글 입력 방해 X).
    useEffect(() => {
        const h = (e: KeyboardEvent) => {
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
            if (e.key === "?") {
                e.preventDefault();
                toggle();
            }
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [toggle]);

    if (!run) return null;
    // 2026-04-23: run 이 캐시되어 있어도 플레이 씬이 아니면 탭 노출 안 함.
    if (currentSceneId && !DRAWER_SCENES.has(currentSceneId)) return null;

    const isFrontier = run.scenario.id === "frontier-vanguard";
    const chapters: ChapterIntroDef[] = getScenarioChapters(run.scenario.id);
    const currentChapter = chapters.find(c => run.currentWeek >= c.startWeek && run.currentWeek <= c.endWeek);
    const upcoming = getScenarioUpcomingIntel(run.scenario.id, run.currentWeek);
    const trajectory = isFrontier ? projectFrontierTrajectory(run.scenarioState) : null;
    const trajectoryFootprints = isFrontier ? summarizeFrontierTrajectoryFootprints(run.eventLog, 2) : [];

    const roleLabel = (() => {
        const tags = run.scenarioState?.outcomeTags ?? [];
        if (tags.includes("role:frontline")) return "전열";
        if (tags.includes("role:quartermaster")) return "병참";
        if (tags.includes("role:scout")) return "정찰";
        return null;
    })();

    const weeksLeft = Math.max(0, run.maxWeeks - run.currentWeek);
    const recentEvents = run.eventLog.slice(-5).reverse();

    return (
        <>
            {/* 우측 엣지 탭 — 드로어 닫혀 있을 때만 보임 (열려 있으면 드로어 자체가 자리 차지). */}
            <AnimatePresence>
                {!open && (
                    <motion.button
                        key="drawer-tab"
                        type="button"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.25, ease: tokens.easing.decelerate }}
                        whileHover={{ x: -2 }}
                        onClick={() => setOpen(true)}
                        className="fixed pointer-events-auto select-none"
                        style={{
                            right: 0,
                            top: "42%",
                            zIndex: tokens.elevation.hud + 2,
                            width: 34,
                            padding: "18px 6px",
                            background: `linear-gradient(180deg, ${tokens.color.ink}E8, ${tokens.color.ink}D4)`,
                            border: `1px solid ${tokens.color.brass}66`,
                            borderRight: "none",
                            borderRadius: "10px 0 0 10px",
                            color: tokens.color.brass,
                            cursor: "pointer",
                            fontSize: 14,
                            letterSpacing: "3px",
                            writingMode: "vertical-rl",
                            textOrientation: "upright",
                            boxShadow: `-8px 0 18px ${tokens.color.ink}88, inset 1px 0 0 ${tokens.color.brass}33`,
                        }}
                        title="원정 장부 펴기 (? 키)"
                        aria-label="원정 장부 열기"
                    >
                        장부
                    </motion.button>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {open && (
                    <>
                        {/* scrim — 바깥 클릭 시 닫힘 */}
                        <motion.div
                            key="drawer-scrim"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.22 }}
                            className="fixed inset-0 cursor-pointer"
                            style={{
                                zIndex: tokens.elevation.modal - 1,
                                background: `${tokens.color.ink}66`,
                                backdropFilter: "blur(2px)",
                            }}
                            onClick={() => setOpen(false)}
                            aria-hidden
                        />

                        <motion.aside
                            key="drawer-panel"
                            initial={{ x: 440 }}
                            animate={{ x: 0 }}
                            exit={{ x: 440 }}
                            transition={{ duration: 0.34, ease: tokens.easing.decelerate }}
                            className="fixed right-0 top-0 bottom-0 flex flex-col"
                            style={{
                                width: 440,
                                background: `linear-gradient(180deg, ${tokens.color.ink}F8, ${tokens.color.ink}EE)`,
                                borderLeft: `1px solid ${tokens.color.brass}55`,
                                boxShadow: `-28px 0 52px ${tokens.color.ink}DD`,
                                zIndex: tokens.elevation.modal,
                            }}
                            role="dialog"
                            aria-label="현재 상황 장부"
                        >
                            {/* 2026-04-23 헤더 폴리시: Spirytale 스타일 + 금속 hair-line. */}
                            <div
                                className="px-l pt-l pb-m relative"
                            >
                                {/* 금속 hair-line */}
                                <div
                                    aria-hidden
                                    className="absolute left-0 right-0 bottom-0"
                                    style={{
                                        height: 1,
                                        background: `linear-gradient(90deg, ${tokens.color.brass}66 0%, ${tokens.color.brass}22 50%, transparent 100%)`,
                                    }}
                                />
                                <div className="flex items-start justify-between gap-s">
                                    <div className="min-w-0">
                                        {/* 2026-04-25: "원정 장부 · 지금의 자리" kicker 제거 — 타이틀 충분. */}
                                        <div
                                            className="font-display truncate"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: "clamp(22px, 1.7vw, 28px)",
                                                letterSpacing: "0.04em",
                                                textShadow: `0 0 18px ${tokens.color.ember}44, 0 1px 0 ${tokens.color.ink}`,
                                            }}
                                        >
                                            {run.characterName}의 원정
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setOpen(false)}
                                        className="rounded-full shrink-0"
                                        style={{
                                            width: 32,
                                            height: 32,
                                            background: `${tokens.color.ink}CC`,
                                            border: `1px solid ${tokens.color.parchment}44`,
                                            color: tokens.color.parchment,
                                            fontSize: 18,
                                            lineHeight: "1",
                                            cursor: "pointer",
                                            boxShadow: `0 0 10px ${tokens.color.ink}44`,
                                        }}
                                        title="닫기 (Esc)"
                                        aria-label="장부 닫기"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div
                                    className="mt-xs font-body flex items-center gap-xs"
                                    style={{
                                        color: tokens.color.parchmentDim,
                                        fontSize: 12,
                                        letterSpacing: tokens.type.letterSpacing.wide,
                                    }}
                                >
                                    {/* 2026-04-25: "씬 이동 없이 상황을 훑는다 · " 플레이버 제거. 키보드 힌트만 남김. */}
                                    <kbd style={{ fontFamily: "inherit", padding: "0 4px", border: `1px solid ${tokens.color.parchment}33`, borderRadius: 3, color: tokens.color.parchmentDim }}>?</kbd>
                                    <span>토글</span>
                                    <span style={{ opacity: 0.4 }}>·</span>
                                    <kbd style={{ fontFamily: "inherit", padding: "0 4px", border: `1px solid ${tokens.color.parchment}33`, borderRadius: 3, color: tokens.color.parchmentDim }}>Esc</kbd>
                                    <span>닫기</span>
                                </div>
                            </div>

                            {/* 본문 */}
                            <div className="flex-1 overflow-y-auto px-l py-m flex flex-col gap-m">
                                <SectionCard title="오늘 어디쯤 있는가" accent={tokens.color.brass}>
                                    <div
                                        className="font-display"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: tokens.type.size.small,
                                            letterSpacing: tokens.type.letterSpacing.wide,
                                        }}
                                    >
                                        {currentChapter?.title ?? run.scenario.name} · {run.currentWeek}주차 / 전체 {run.maxWeeks}주
                                    </div>
                                    <div
                                        className="mt-[4px] font-body"
                                        style={{
                                            color: tokens.color.parchment,
                                            fontSize: 13.5,
                                            lineHeight: tokens.type.lineHeight.normal,
                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                        }}
                                    >
                                        {weeksLeft > 0
                                            ? `남은 ${weeksLeft}주`
                                            : "이번 주가 이 여정의 마지막 주입니다."}
                                        {roleLabel ? ` · ${roleLabel}의 자리에서.` : ""}
                                    </div>
                                    {currentChapter?.subtitle && (
                                        <div
                                            className="mt-s font-body"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: 13.5,
                                                lineHeight: tokens.type.lineHeight.normal,
                                                fontStyle: "italic",
                                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                            }}
                                        >
                                            {currentChapter.subtitle}
                                        </div>
                                    )}
                                </SectionCard>

                                {trajectory && (
                                    <SectionCard title="엔딩 궤적" accent={resolveTrajectoryAccent(trajectory.tone)}>
                                        <EndingCompass
                                            scenarioId={run.scenario.id}
                                            scenarioState={run.scenarioState}
                                            compact={false}
                                            recentFootprints={trajectoryFootprints}
                                        />
                                    </SectionCard>
                                )}

                                {upcoming && (
                                    <SectionCard title="다음 예고된 장면" accent={tokens.color.ember}>
                                        <div
                                            className="font-body"
                                            style={{
                                                color: tokens.color.ember,
                                                fontSize: 13.5,
                                                letterSpacing: tokens.type.letterSpacing.wide,
                                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                            }}
                                        >
                                            {formatScenarioIntelEta(run.currentWeek, upcoming.atWeek)} · {upcoming.kind}
                                        </div>
                                        <div
                                            className="mt-[4px] font-display"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: tokens.type.size.small,
                                                letterSpacing: tokens.type.letterSpacing.wide,
                                            }}
                                        >
                                            {upcoming.title}
                                        </div>
                                        <div
                                            className="mt-[4px] font-body"
                                            style={{
                                                color: tokens.color.parchment,
                                                fontSize: 13.5,
                                                lineHeight: tokens.type.lineHeight.normal,
                                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                            }}
                                        >
                                            {upcoming.summary}
                                        </div>
                                        {upcoming.prep.length > 0 && (
                                            <div className="mt-s flex flex-wrap gap-xs">
                                                {upcoming.prep.map(p => (
                                                    <span
                                                        key={p}
                                                        className="rounded-pill px-s py-[2px] font-body"
                                                        style={{
                                                            color: tokens.color.parchment,
                                                            background: `${tokens.color.ink}99`,
                                                            border: `1px solid ${tokens.color.parchment}33`,
                                                            fontSize: 14,
                                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                                        }}
                                                    >
                                                        준비: {p}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </SectionCard>
                                )}

                                {isFrontier && run.scenarioState && (
                                    <SectionCard title="원정 상태" accent={tokens.color.steel}>
                                        <StateRow label="원정 소모" value={`${run.scenarioState.expeditionWear}`}
                                            tone={run.scenarioState.expeditionWear >= 65 ? "danger" : run.scenarioState.expeditionWear >= 40 ? "warn" : "steady"}
                                        />
                                        <StateRow label="보급선" value={supplyShortLabel(run.scenarioState.supplyLineState)}
                                            tone={run.scenarioState.supplyLineState === "collapsed" ? "danger" : run.scenarioState.supplyLineState === "strained" ? "warn" : "steady"}
                                        />
                                        <StateRow label="병사 사기" value={signedStr(run.scenarioState.troopMorale)}
                                            tone={run.scenarioState.troopMorale <= -10 ? "danger" : run.scenarioState.troopMorale < 0 ? "warn" : "good"}
                                        />
                                        <StateRow label="변경 신뢰" value={signedStr(run.scenarioState.frontierTrust)}
                                            tone={run.scenarioState.frontierTrust <= -10 ? "danger" : run.scenarioState.frontierTrust < 0 ? "warn" : "good"}
                                        />
                                        <StateRow label="추모의 무게" value={`${run.scenarioState.memorialWeight}`}
                                            tone={run.scenarioState.memorialWeight >= 5 ? "warn" : "steady"}
                                        />
                                        <StateRow label="그림자 빚" value={`${run.scenarioState.supplyDebt}`}
                                            tone={run.scenarioState.supplyDebt >= 3 ? "danger" : run.scenarioState.supplyDebt > 0 ? "warn" : "steady"}
                                        />
                                    </SectionCard>
                                )}

                                {/* 2026-04-23 v2: 훈련·연구 진행도 — 현재 런에서 쌓은 서/혼. */}
                                <TrainingProgressPanel runId={run.id} hideZero={false} />

                                {isFrontier && (run.scenarioCompanions ?? []).length > 0 && (
                                    <SectionCard
                                        title={`원정 인원 · ${summarizeCompanions(run.scenarioCompanions ?? [], run.companionStates ?? [])}`}
                                        accent={tokens.color.moss}
                                    >
                                        {(() => {
                                            const orderedCompanions = sortCompanionsByRelevance(
                                                run.scenarioCompanions ?? [],
                                                run.companionStates ?? [],
                                                run.currentWeek,
                                            );
                                            const closest = summarizeClosestCompanion(run.scenarioCompanions ?? [], run.companionStates ?? []);
                                            return (
                                                <>
                                                    {closest ? (
                                                        <div
                                                            className="mb-s rounded-s px-s py-[5px] font-body"
                                                            style={{
                                                                color: tokens.color.parchment,
                                                                background: `${tokens.color.ink}88`,
                                                                border: `1px solid ${tokens.color.parchment}22`,
                                                                fontSize: 15.5,
                                                                lineHeight: tokens.type.lineHeight.normal,
                                                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                                            }}
                                                        >
                                                            <span style={{ color: tokens.color.brass }}>지금 가장 가까운 이름</span>
                                                            {" · "}
                                                            {closest}
                                                        </div>
                                                    ) : null}
                                                    <div className="flex flex-col gap-xs">
                                                        {orderedCompanions.map(c => (
                                                            <CompanionRow
                                                                key={c.id}
                                                                companion={c}
                                                                state={(run.companionStates ?? []).find(s => s.companionId === c.id)}
                                                                currentWeek={run.currentWeek}
                                                                scenarioState={run.scenarioState}
                                                            />
                                                        ))}
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </SectionCard>
                                )}

                                {recentEvents.length > 0 && (
                                    <SectionCard title="최근 사건" accent={tokens.color.parchment}>
                                        <div className="flex flex-col gap-[6px]">
                                            {recentEvents.map((e, i) => (
                                                <div
                                                    key={`${e.weekNo}-${e.eventOrder}-${i}`}
                                                    className="flex items-baseline gap-s"
                                                >
                                                    <span
                                                        className="font-stat shrink-0 whitespace-nowrap"
                                                        style={{
                                                            color: tokens.color.parchment,
                                                            fontSize: tokens.type.size.small,
                                                            width: 38,
                                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                                        }}
                                                    >
                                                        {e.weekNo}주
                                                    </span>
                                                    <span
                                                        className="font-body truncate"
                                                        style={{
                                                            color: tokens.color.parchment,
                                                            fontSize: tokens.type.size.small,
                                                            lineHeight: tokens.type.lineHeight.snug,
                                                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                                        }}
                                                        title={e.message}
                                                    >
                                                        {e.title}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </SectionCard>
                                )}
                            </div>

                            {/* Footer CTA */}
                            <div
                                className="px-l py-m flex gap-s"
                                style={{ borderTop: `1px solid ${tokens.color.parchment}22` }}
                            >
                                <DrawerCTAButton
                                    onClick={() => {
                                        setOpen(false);
                                        void push("character-sheet", { runId: run.id });
                                    }}
                                    label="인물 기록으로"
                                    accent={tokens.color.brass}
                                />
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}

// ────────────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────────────

function SectionCard({
    title,
    accent,
    children,
}: {
    title: string;
    accent: string;
    children: ReactNode;
}) {
    // 2026-04-23: 섹션 카드 폴리시 — 배경 그라디언트 + accent hair-line + uppercase kicker.
    return (
        <div
            className="rounded-m px-m py-s relative"
            style={{
                background: `linear-gradient(180deg, ${tokens.color.ink}7A 0%, ${tokens.color.ink}58 100%)`,
                border: `1px solid ${accent}3A`,
                boxShadow: `inset 0 1px 0 ${accent}22, 0 2px 8px ${tokens.color.ink}44`,
            }}
        >
            <div
                className="font-display mb-s flex items-center gap-xs"
                style={{
                    color: accent,
                    fontSize: 13.5,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                <span
                    aria-hidden
                    style={{
                        width: 8,
                        height: 1,
                        background: accent,
                        boxShadow: `0 0 6px ${accent}AA`,
                        opacity: 0.7,
                    }}
                />
                {title}
            </div>
            {children}
        </div>
    );
}

type StateRowTone = "good" | "steady" | "warn" | "danger";

function StateRow({ label, value, tone }: { label: string; value: string; tone: StateRowTone }) {
    const toneColor = tone === "danger"
        ? tokens.color.burgundy
        : tone === "warn"
            ? tokens.color.ember
            : tone === "good"
                ? tokens.color.moss
                : tokens.color.parchment;
    return (
        <div
            className="flex items-baseline justify-between gap-s py-[3px]"
            style={{ borderBottom: `1px solid ${tokens.color.parchment}14` }}
        >
            <span
                className="font-body"
                style={{
                    color: tokens.color.parchment,
                    fontSize: tokens.type.size.small,
                    letterSpacing: tokens.type.letterSpacing.wide,
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                {label}
            </span>
            <span
                className="font-stat"
                style={{
                    color: toneColor,
                    fontSize: tokens.type.size.small,
                    letterSpacing: tokens.type.letterSpacing.wide,
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                {value}
            </span>
        </div>
    );
}

function CompanionRow({
    companion,
    state,
    currentWeek,
    scenarioState,
}: {
    companion: ScenarioCompanion;
    state?: ScenarioCompanionState;
    currentWeek: number;
    scenarioState?: ScenarioStateResponse | null;
}) {
    const status = state?.status ?? "active";
    const meta = companionStatusMeta(status);
    const recentlyChanged = state?.lastChangedWeek != null && state.lastChangedWeek === currentWeek;
    // 2026-04-24 축 2: 서버 notes + outcomeTag 기반 derived memory 를 합쳐서 전체 회상 스택 구성.
    // 동일 라인 중복 제거. 서버 notes 가 이미 충분히 풍부하면 derivation 은 생략됨.
    const serverNotes = state?.notes ?? [];
    const derivedMemories = memoriesForCompanion(scenarioState ?? null, companion.id);
    const mergedMemories: string[] = [];
    const seen = new Set<string>();
    for (const n of serverNotes) {
        if (n && !seen.has(n)) {
            mergedMemories.push(n);
            seen.add(n);
        }
    }
    for (const m of derivedMemories) {
        if (m.line && !seen.has(m.line)) {
            mergedMemories.push(m.line);
            seen.add(m.line);
        }
    }
    const latestNote = mergedMemories.at(-1) ?? null;
    const relationshipSummary = summarizeCompanionRelationship(companion, state, mergedMemories);
    const statusEffect = companion.statusEffects?.[status];
    const badges = buildCompanionBadges(companion, state, currentWeek, mergedMemories);
    const [expanded, setExpanded] = useState(false);
    return (
        <div
            className="flex items-start gap-s rounded-s px-xs py-[6px]"
            style={{
                background: recentlyChanged ? `${tokens.color.brass}12` : "transparent",
                border: recentlyChanged ? `1px solid ${tokens.color.brass}33` : "1px solid transparent",
                boxShadow: recentlyChanged ? `inset 0 1px 0 ${tokens.color.brass}22, 0 0 14px ${tokens.color.brass}12` : "none",
            }}
        >
            <span
                aria-hidden
                className="rounded-full shrink-0"
                style={{
                    width: 8,
                    height: 8,
                    background: meta.color,
                    boxShadow: `0 0 6px ${meta.color}AA`,
                }}
            />
            <div className="flex-1 min-w-0">
                <div
                    className="font-body truncate"
                    style={{
                        color: recentlyChanged ? tokens.color.brass : tokens.color.parchment,
                        fontSize: tokens.type.size.small,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    {companion.displayName}
                </div>
                <div
                    className="font-body truncate"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: 15,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    {companion.role} · {meta.label}
                </div>
                {badges.length > 0 && (
                    <div className="mt-[4px] flex flex-wrap gap-[4px]">
                        {badges.map((badge) => (
                            <span
                                key={badge.label}
                                className="rounded-pill px-[6px] py-[2px] font-body"
                                style={{
                                    color: badge.color,
                                    background: `${badge.color}14`,
                                    border: `1px solid ${badge.color}33`,
                                    fontSize: 13,
                                    letterSpacing: tokens.type.letterSpacing.wide,
                                }}
                            >
                                {badge.label}
                            </span>
                        ))}
                    </div>
                )}
                {status !== "active" && statusEffect && (
                    <div
                        className="mt-[6px] rounded-s px-s py-[6px]"
                        style={{
                            background: `${meta.color}12`,
                            border: `1px solid ${meta.color}33`,
                        }}
                    >
                        <div
                            className="font-display"
                            style={{
                                color: meta.color,
                                fontSize: 13,
                                letterSpacing: tokens.type.letterSpacing.wide,
                            }}
                        >
                            {statusEffect.label ?? meta.label}
                        </div>
                        {statusEffect.effect && (
                            <div
                                className="mt-[2px] font-body"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: 14,
                                    lineHeight: tokens.type.lineHeight.normal,
                                }}
                            >
                                {statusEffect.effect}
                            </div>
                        )}
                        {statusEffect.note && (
                            <div
                                className="mt-[3px] font-body"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: 13,
                                    lineHeight: tokens.type.lineHeight.normal,
                                    fontStyle: "italic",
                                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                }}
                            >
                                {statusEffect.note}
                            </div>
                        )}
                    </div>
                )}
                  <div className="flex items-center gap-[6px] mt-[4px]">
                    <span
                        className="font-body"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: 13,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        친밀
                    </span>
                    <div
                        aria-label={`친밀도 ${state?.affinity ?? 0}`}
                        className="flex items-center gap-[3px]"
                    >
                        {Array.from({ length: 4 }, (_, index) => {
                            const filled = index < Math.max(0, Math.min(4, state?.affinity ?? 0));
                            return (
                                <span
                                    key={index}
                                    aria-hidden
                                    className="rounded-full"
                                    style={{
                                        width: 6,
                                        height: 6,
                                        background: filled ? tokens.color.brass : `${tokens.color.parchment}33`,
                                        boxShadow: filled ? `0 0 6px ${tokens.color.brass}99` : "none",
                                    }}
                                />
                            );
                        })}
                      </div>
                  </div>
                  {(latestNote || relationshipSummary) && !expanded && (
                      <div
                          className="mt-[6px] rounded-s px-s py-[5px] font-body"
                          style={{
                              color: tokens.color.parchment,
                              background: `${tokens.color.ink}88`,
                              border: `1px solid ${recentlyChanged ? tokens.color.brass : tokens.color.parchment}22`,
                              fontSize: 15,
                              lineHeight: tokens.type.lineHeight.normal,
                              textShadow: `${tokens.color.ink} 0px 2px 8px`,
                          }}
                      >
                          {relationshipSummary && (
                              <div
                                  className="mb-[4px] font-display"
                                  style={{
                                      color: tokens.color.brass,
                                      fontSize: 13,
                                      letterSpacing: tokens.type.letterSpacing.wide,
                                      textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                  }}
                              >
                                  관계의 결 · {relationshipSummary}
                              </div>
                          )}
                          {latestNote ? latestNote : "아직 함께한 장면은 많지 않지만, 이 이름은 이제 장부 안에서 따로 눈에 들어오기 시작합니다."}
                          {mergedMemories.length > 1 && (
                              <button
                                  type="button"
                                  onClick={() => setExpanded(true)}
                                  className="ml-xs rounded-s px-[4px] py-[1px] cursor-pointer"
                                  style={{
                                      color: tokens.color.brass,
                                      background: `${tokens.color.brass}12`,
                                      border: `1px solid ${tokens.color.brass}44`,
                                      fontSize: 13,
                                      letterSpacing: tokens.type.letterSpacing.wide,
                                      textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                  }}
                                  title="이 인원과 나눈 모든 순간 펼치기"
                              >
                                  +{mergedMemories.length - 1} 기록 · 펼치기
                              </button>
                          )}
                      </div>
                  )}
                  {expanded && mergedMemories.length > 0 && (
                      <div
                          className="mt-[6px] rounded-s p-[6px] flex flex-col gap-[4px]"
                          style={{
                              background: `${tokens.color.ink}AA`,
                              border: `1px solid ${tokens.color.brass}33`,
                          }}
                      >
                          <div className="flex items-center justify-between mb-[2px]">
                              <span
                                  className="font-display"
                                  style={{
                                      color: tokens.color.brass,
                                      fontSize: 13,
                                      letterSpacing: tokens.type.letterSpacing.wide,
                                  }}
                              >
                                  함께한 순간 · {mergedMemories.length}
                              </span>
                              <button
                                  type="button"
                                  onClick={() => setExpanded(false)}
                                  className="rounded-s px-[4px] py-[1px] cursor-pointer"
                                  style={{
                                      color: tokens.color.parchment,
                                      background: "transparent",
                                      border: `1px solid ${tokens.color.parchment}33`,
                                      fontSize: 13,
                                      letterSpacing: tokens.type.letterSpacing.wide,
                                      textShadow: `${tokens.color.ink} 0px 2px 8px`,
                                  }}
                                  title="접기"
                              >
                                  접기
                              </button>
                          </div>
                          {mergedMemories.map((line, i) => (
                              <div
                                  key={`${i}-${line.slice(0, 12)}`}
                                  className="rounded-[3px] px-[6px] py-[4px] font-body"
                                  style={{
                                      color: tokens.color.parchment,
                                      background: `${tokens.color.ink}66`,
                                      borderLeft: `2px solid ${i === mergedMemories.length - 1 ? tokens.color.brass : tokens.color.parchment}55`,
                                      fontSize: 14,
                                      lineHeight: tokens.type.lineHeight.normal,
                                      fontStyle: "italic",
                                  }}
                              >
                                  {line}
                              </div>
                          ))}
                      </div>
                  )}
              </div>
          </div>
      );
}

function DrawerCTAButton({
    onClick,
    label,
    accent,
}: {
    onClick: () => void;
    label: string;
    accent: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex-1 rounded-m px-m py-s font-display"
            style={{
                background: `${accent}1A`,
                border: `1px solid ${accent}55`,
                color: accent,
                fontSize: tokens.type.size.small,
                letterSpacing: tokens.type.letterSpacing.wide,
                cursor: "pointer",
            }}
        >
            {label}
        </button>
    );
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

function supplyShortLabel(state: string): string {
    switch (state) {
        case "stable": return "안정";
        case "delayed": return "지연";
        case "strained": return "경색";
        case "collapsed": return "붕괴";
        default: return state;
    }
}

function summarizeCompanionRelationship(
    companion: ScenarioCompanion,
    state: ScenarioCompanionState | undefined,
    memories: string[],
): string | null {
    const affinity = state?.affinity ?? 0;
    const status = state?.status ?? "active";
    if (status === "fallen") return "끝내 장부에 먼저 남은 이름";
    if (status === "injured") return affinity >= 3 ? "상처를 함께 넘긴 사람" : "지금은 숨을 붙드는 사람";
    if (memories.length >= 4) return affinity >= 4 ? "이제 서로의 판단을 아는 사이" : "몇 번의 밤을 함께 넘긴 동료";
    if (memories.length >= 2) return affinity >= 3 ? "속내를 조금 아는 동료" : "기억이 쌓이기 시작한 동료";
    if (affinity >= 2) return `${companion.role}의 자리를 믿어 보는 중`;
    return null;
}

function summarizeClosestCompanion(
    companions: ScenarioCompanion[],
    companionStates: ScenarioCompanionState[],
): string | null {
    if (!companions.length) return null;
    const stateById = new Map(companionStates.map(state => [state.companionId, state]));
    const ranked = companions
        .map(companion => ({
            companion,
            state: stateById.get(companion.id),
        }))
        .sort((a, b) => {
            const affinityDiff = (b.state?.affinity ?? 0) - (a.state?.affinity ?? 0);
            if (affinityDiff !== 0) return affinityDiff;
            return (b.state?.lastChangedWeek ?? 0) - (a.state?.lastChangedWeek ?? 0);
        });
    const top = ranked[0];
    if (!top) return null;
    const affinity = top.state?.affinity ?? 0;
    if (top.state?.status === "fallen") return `${top.companion.displayName}은 끝내 가장 먼저 장부에 남았습니다.`;
    if (affinity >= 4) return `${top.companion.displayName}은 이제 서로의 판단을 오래 공유한 동료입니다.`;
    if (affinity >= 2) return `${top.companion.displayName}은 이번 원정에서 가장 자주 곁을 지킨 이름입니다.`;
    return `${top.companion.displayName}의 자리가 가장 먼저 기억에 남기 시작했습니다.`;
}

function buildCompanionBadges(
    companion: ScenarioCompanion,
    state: ScenarioCompanionState | undefined,
    currentWeek: number,
    memories: string[],
): Array<{ label: string; color: string }> {
    const badges: Array<{ label: string; color: string }> = [];
    const affinity = state?.affinity ?? 0;
    const status = state?.status ?? "active";

    if (state?.lastChangedWeek === currentWeek) {
        badges.push({ label: "이번 주 변화", color: tokens.color.brass });
    }
    if (status === "fallen") {
        badges.push({ label: "추모 대상", color: tokens.color.burgundy });
    } else if (status === "injured") {
        badges.push({ label: "부상", color: tokens.color.ember });
    }
    if (affinity >= 4) {
        badges.push({ label: "핵심 동료", color: tokens.color.moss });
    } else if (affinity >= 2) {
        badges.push({ label: "가까운 이름", color: tokens.color.steel });
    }
    if (memories.length >= 4) {
        badges.push({ label: "기억 누적", color: tokens.color.parchment });
    }
    if (companion.role.includes("부관") || companion.role.includes("의무관")) {
        badges.push({ label: "핵심 보직", color: tokens.color.brass });
    }
    return badges.slice(0, 4);
}

function sortCompanionsByRelevance(
    companions: ScenarioCompanion[],
    companionStates: ScenarioCompanionState[],
    currentWeek: number,
): ScenarioCompanion[] {
    const stateById = new Map(companionStates.map(state => [state.companionId, state]));
    return [...companions].sort((a, b) => {
        const aState = stateById.get(a.id);
        const bState = stateById.get(b.id);
        const aRecent = aState?.lastChangedWeek === currentWeek ? 1 : 0;
        const bRecent = bState?.lastChangedWeek === currentWeek ? 1 : 0;
        if (bRecent !== aRecent) return bRecent - aRecent;
        const aAffinity = aState?.affinity ?? 0;
        const bAffinity = bState?.affinity ?? 0;
        if (bAffinity !== aAffinity) return bAffinity - aAffinity;
        const aWeight = relevanceStatusWeight(aState?.status ?? "active");
        const bWeight = relevanceStatusWeight(bState?.status ?? "active");
        if (bWeight !== aWeight) return bWeight - aWeight;
        return a.displayName.localeCompare(b.displayName, "ko");
    });
}

function relevanceStatusWeight(status: string): number {
    switch (status) {
        case "fallen": return 3;
        case "injured": return 2;
        default: return 1;
    }
}

function signedStr(value: number): string {
    if (value > 0) return `+${value}`;
    return `${value}`;
}

function companionStatusMeta(status: string): { label: string; color: string } {
    switch (status) {
        case "active": return { label: "건재", color: tokens.color.moss };
        case "injured": return { label: "부상", color: tokens.color.ember };
        case "fallen": return { label: "쓰러짐", color: tokens.color.burgundy };
        default: return { label: status, color: tokens.color.parchment };
    }
}

function summarizeCompanions(
    companions: ScenarioCompanion[],
    states: ScenarioCompanionState[],
): string {
    const byId = new Map(states.map(s => [s.companionId, s.status]));
    let active = 0, injured = 0, fallen = 0;
    for (const c of companions) {
        const s = byId.get(c.id) ?? "active";
        if (s === "fallen") fallen++;
        else if (s === "injured") injured++;
        else active++;
    }
    const parts: string[] = [];
    if (active > 0) parts.push(`건재 ${active}`);
    if (injured > 0) parts.push(`부상 ${injured}`);
    if (fallen > 0) parts.push(`쓰러짐 ${fallen}`);
    return parts.join(" · ") || "편제 미정";
}

function resolveTrajectoryAccent(tone: "ember" | "moss" | "steel" | "burgundy" | "parchment"): string {
    switch (tone) {
        case "ember": return tokens.color.ember;
        case "moss": return tokens.color.moss;
        case "steel": return tokens.color.steel;
        case "burgundy": return tokens.color.burgundy;
        case "parchment":
        default: return tokens.color.parchment;
    }
}
